import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PersonIcon from '@mui/icons-material/Person';
import {
  Avatar,
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import { UserPermissionDto } from '@/api/generated';
import { logout } from '@/api/utils/logoutRequest.util.ts';
import logo from '@/assets/img/logo.png';
import {
  MENU_ITEMS,
  MenuItemEnum,
} from '@/components/layout/sidebar/sideBarMenu/model/SideBarMenuItems.model.tsx';
import { ContactMenuItem } from '@/components/layout/topBar/components/ContactMenuItem.comp.tsx';
import { NewsMenuItem } from '@/components/layout/topBar/components/NewsMenuItem.comp.tsx';
import {
  OverflowNavBar,
  OverflowNavItemConfig,
} from '@/components/layout/topBar/components/OverflowNavBar.comp.tsx';
import { TopBarLanguageChange } from '@/components/layout/topBar/components/TopBarLanguageChange/TopBarLanguageChange.comp.tsx';
import { TopBarNotificationDrawer } from '@/components/layout/topBar/components/TopBarNotificationDrawer/TopBarNotificationDrawer.comp';
import { useThemeMode } from '@/config/theme/ThemeMode.context.tsx';
import { TOP_BAR_HEIGHT, TOP_BAR_Z_INDEX } from '@/config/theme/uiTokens.ts';
import { usePageContext } from '@/contexts/pageContext/Page.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { hasRouteAccess } from '@/utils/permissions.util.ts';
import { useKnowMeNavigate } from '@/utils/useKnowMeNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

export const TopBar: React.FC = () => {
  const { employeeProfileId, firstName, lastName, permissions, roles } = useUserContext();
  const { push } = useKnowMeNavigate();
  const { t } = useTranslationWithPrefix('topBar');
  const { t: tMenu } = useTranslationWithPrefix('menu.item');
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const { setPageDetails } = usePageContext();
  const { pathname } = useLocation();
  const isStartSelected = useMatch(RouteEnum.ROOT) != null;
  const isMyProfileSelected = useMatch(`${RouteEnum.MY_PROFILE}`) != null;

  const hasSelfProfilePermission = permissions.includes(UserPermissionDto.EmployeesSelfProfile);
  const showMyProfileTab = hasSelfProfilePermission && !!employeeProfileId;

  const [isScrolled, setIsScrolled] = useState(false);
  const [avatarAnchorEl, setAvatarAnchorEl] = useState<HTMLElement | null>(null);
  const isAvatarMenuOpen = Boolean(avatarAnchorEl);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    handleAvatarMenuClose();
    await logout();
  };

  const handleAvatarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarAnchorEl(event.currentTarget);
  };

  const handleAvatarMenuClose = () => {
    setAvatarAnchorEl(null);
  };

  const goToLandingPage = () => {
    push(RouteEnum.ROOT);
  };

  const handleGoToProfile = () => {
    if (!employeeProfileId) return;
    handleAvatarMenuClose();
    push(RouteEnum.MY_PROFILE_DASHBOARD);
  };

  const goToManual = () => {
    handleAvatarMenuClose();
    window.open('https://sway.cloud.microsoft/AYIgTbMosostRqCt?ref=Link', '_blank');
  };

  const goToSurvey = () => {
    handleAvatarMenuClose();
    window.open('https://forms.office.com/e/0hxEW1fXcC', '_blank');
  };

  const handleThemeSelect = (nextMode: 'dark' | 'light') => {
    if (mode !== nextMode) {
      toggleMode();
    }
  };

  const initials =
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim().toUpperCase() ||
    firstName?.slice(0, 2).toUpperCase() ||
    '?';

  const userPermList = permissions as unknown as string[];
  const userRoleList = (roles as unknown as string[]) ?? [];

  const navItems: OverflowNavItemConfig[] = [
    {
      id: 'start',
      isActive: isStartSelected,
      label: t('start'),
      onClick: goToLandingPage,
    },
    ...(showMyProfileTab
      ? [
          {
            id: 'myProfile',
            isActive: isMyProfileSelected,
            label: t('profile'),
            onClick: () => push(RouteEnum.MY_PROFILE_DASHBOARD),
          },
        ]
      : []),
    ...MENU_ITEMS.flatMap(group => group.items)
      .filter(item =>
        hasRouteAccess(userPermList, userRoleList, item.availableFor, item.availableForRoles),
      )
      .map(item => ({
        id: item.id,
        isActive: pathname.startsWith(item.route.startsWith('/') ? item.route : `/${item.route}`),
        label: tMenu(item.id),
        onClick: () => {
          push(item.route);
          setPageDetails({ title: item.id });
        },
      })),
  ];

  return (
    <Box
      component="header"
      sx={{
        alignItems: 'center',
        backdropFilter: isScrolled ? 'none' : 'blur(12px)',
        backgroundColor: theme.colors.bgTopbar,
        borderBottom: `1px solid ${theme.colors.topbarBorder}`,
        display: 'flex',
        height: `${TOP_BAR_HEIGHT}px`,
        left: 0,
        position: 'fixed',
        px: { lg: 4, xs: 2 },
        right: 0,
        top: 0,
        transition:
          'border-color 0.2s cubic-bezier(0.4,0,0.2,1), backdrop-filter 0.2s cubic-bezier(0.4,0,0.2,1)',
        zIndex: TOP_BAR_Z_INDEX,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          onClick={goToLandingPage}
          spacing={1}
          sx={{
            '&:hover': {
              filter: 'drop-shadow(0 4px 14px rgba(145,188,247,0.18))',
            },

            alignItems: 'center',

            cursor: 'pointer',
            flexShrink: 0,
            height: '100%',
            transition: 'filter 0.2s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <img
            alt="biedronka logo"
            src={logo}
            style={{
              filter: 'none',
              height: 36,
              width: 'auto',
            }}
          />
        </Stack>

        <OverflowNavBar items={navItems} />

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            flexShrink: 0,
            gap: 2.5,
            ml: 'auto',
          }}
        >
          <Box sx={{ alignItems: 'center', display: { md: 'flex', xs: 'none' }, gap: 1 }}>
            <NewsMenuItem />
            <ContactMenuItem />
          </Box>
          <TopBarNotificationDrawer />
          <Avatar
            onClick={handleAvatarMenuOpen}
            sx={{
              '&:hover': {
                boxShadow: theme.colors.shadowGlow,
              },
              background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentDark})`,
              border: 'none',
              color: theme.colors.darkCharcoal,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 700,
              height: 36,
              transition: 'box-shadow 0.2s cubic-bezier(0.4,0,0.2,1)',
              width: 36,
            }}
          >
            {initials}
          </Avatar>
          <Menu
            anchorEl={avatarAnchorEl}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            autoFocus={false}
            onClose={handleAvatarMenuClose}
            open={isAvatarMenuOpen}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: theme.colors.bgCard,
                  backgroundImage: 'none',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '16px',
                  boxShadow: theme.colors.shadowCard,
                  mt: 1.5,
                  overflow: 'hidden',
                  width: 260,
                },
              },
            }}
            sx={{
              '& .MuiList-root': {
                p: 0,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <Box sx={{ borderBottom: `1px solid ${theme.colors.border}`, px: 2.5, py: 2 }}>
              <Typography
                sx={{ color: theme.colors.textPrimary, fontSize: '14px', fontWeight: 600 }}
              >
                {[firstName, lastName].filter(Boolean).join(' ')}
              </Typography>
            </Box>
            {showMyProfileTab && (
              <MenuItem
                onClick={handleGoToProfile}
                sx={{
                  '&:hover': { backgroundColor: theme.colors.bgHover },
                  color: theme.colors.textSecondary,
                  gap: 1.5,
                  px: 2.5,
                  py: 1.25,
                }}
              >
                <PersonIcon sx={{ fontSize: '18px' }} />
                {t('profile')}
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                push(RouteEnum.NEWS);
                handleAvatarMenuClose();
              }}
              sx={{
                '&:hover': { backgroundColor: theme.colors.bgHover },
                color: theme.colors.textSecondary,
                display: { md: 'none', xs: 'flex' },
                gap: 1.5,
                px: 2.5,
                py: 1.25,
              }}
            >
              <NewspaperIcon sx={{ fontSize: '18px' }} />
              {tMenu(MenuItemEnum.NEWS)}
            </MenuItem>
            <MenuItem
              onClick={() => {
                push(RouteEnum.CONTACT);
                handleAvatarMenuClose();
              }}
              sx={{
                '&:hover': { backgroundColor: theme.colors.bgHover },
                borderBottom: `1px solid ${theme.colors.border}`,
                color: theme.colors.textSecondary,
                display: { md: 'none', xs: 'flex' },
                gap: 1.5,
                px: 2.5,
                py: 1.25,
              }}
            >
              <ArrowOutwardIcon sx={{ fontSize: '18px' }} />
              {tMenu(MenuItemEnum.CONTACT)}
            </MenuItem>
            <MenuItem
              onClick={goToManual}
              sx={{
                '&:hover': { backgroundColor: theme.colors.bgHover },
                color: theme.colors.textSecondary,
                gap: 1.5,
                px: 2.5,
                py: 1.25,
              }}
            >
              <MenuBookIcon sx={{ fontSize: '18px' }} />
              {t('instruction')}
            </MenuItem>
            <MenuItem
              onClick={goToSurvey}
              sx={{
                '&:hover': { backgroundColor: theme.colors.bgHover },
                color: theme.colors.textSecondary,
                gap: 1.5,
                px: 2.5,
                py: 1.25,
              }}
            >
              <FeedbackIcon sx={{ fontSize: '18px' }} />
              {t('feedback')}
            </MenuItem>
            <MenuItem
              disableRipple
              sx={{
                '&:hover': { backgroundColor: 'transparent' },
                borderTop: `1px solid ${theme.colors.border}`,
                color: theme.colors.textSecondary,
                cursor: 'default',
                justifyContent: 'space-between',
                px: 2.5,
                py: 1.25,
              }}
            >
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{t('language')}</Typography>
              <TopBarLanguageChange variant="menuItem" />
            </MenuItem>
            <MenuItem
              disableRipple
              sx={{
                '&:hover': { backgroundColor: 'transparent' },
                color: theme.colors.textSecondary,
                cursor: 'default',
                justifyContent: 'space-between',
                px: 2.5,
                py: 1.25,
              }}
            >
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{t('theme.label')}</Typography>
              <Stack direction="row" spacing={0.5}>
                {[
                  {
                    icon: <DarkModeRoundedIcon sx={{ fontSize: 16 }} />,
                    label: t('theme.dark'),
                    value: 'dark' as const,
                  },
                  {
                    icon: <LightModeRoundedIcon sx={{ fontSize: 16 }} />,
                    label: t('theme.light'),
                    value: 'light' as const,
                  },
                ].map(option => {
                  const isSelected = mode === option.value;

                  return (
                    <ButtonBase
                      aria-label={option.label}
                      key={option.value}
                      onClick={() => handleThemeSelect(option.value)}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.colors.bgHover,
                          opacity: 1,
                        },
                        alignItems: 'center',
                        backgroundColor: isSelected ? theme.colors.accentBg : 'transparent',
                        border: `1px solid ${
                          isSelected ? theme.colors.accentBorder : theme.colors.border
                        }`,
                        borderRadius: '999px',
                        color: isSelected ? theme.colors.accentInk : theme.colors.textSecondary,
                        display: 'flex',
                        height: 28,
                        justifyContent: 'center',
                        opacity: isSelected ? 1 : 0.5,
                        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                        width: 28,
                      }}
                    >
                      {option.icon}
                    </ButtonBase>
                  );
                })}
              </Stack>
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                '&:hover': { backgroundColor: theme.colors.bgHover },
                borderTop: `1px solid ${theme.colors.border}`,
                color: theme.colors.red,
                gap: 1.5,
                px: 2.5,
                py: 1.25,
              }}
            >
              <LogoutIcon sx={{ fontSize: '18px' }} />
              {t('logout')}
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
    </Box>
  );
};
